import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Button, Checkbox, Container, FormControl, Grid, InputLabel, MenuItem, Paper, Select, Typography, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions } from '@mui/material';
import { styled } from '@mui/material/styles';

// Import modularized utilities from scorecarddata folder
import { normalizeInput } from './scorecarddata/normalizeUtils';
import { validateMove, checkDuplicates } from './scorecarddata/validationUtils';
import { getDifficultyForSymbol, calculateDifficultyScores, calculateTotalDifficulty } from './scorecarddata/difficultyCalculator';
import { validateRequirements } from './scorecarddata/requirementsValidator';

// Import JSON data from scorecarddata folder
import exerciseDataRaw from './scorecarddata/dataExercises.json';
import difficultyDataRaw from './scorecarddata/dataDifficulty.json';
import requirementsDataRaw from './scorecarddata/exerciseRequirements.json';

// Import types from scorecarddata folder
import { ExerciseData, DifficultyData, RequirementsData } from './scorecarddata/types';
import { Exercise } from '../services/competitionCardsService';

import { getAllowedExerciseIdsByClassName  } from './scorecarddata/exerciseRules';


// Type assertion for imported data
const typedExerciseData = exerciseDataRaw as ExerciseData;
const typedDifficultyData = difficultyDataRaw as DifficultyData;
const typedRequirementsData = requirementsDataRaw as RequirementsData;

// Interface für initiale Daten
interface InitialScorecardData {
  exercises: Exercise[];
  selectedRoutines?: string[];
  checkboxes?: boolean[];
  additionalMarkings?: boolean[];
  validationStatus?: {
    isComplete: boolean;
    isFirstExerciseValid: boolean;
    isFullyValid: boolean;
    isFirstExerciseSelected: boolean;
  };
}

// Interface für Props
interface TrampolineScorecardProps {
  initialData?: InitialScorecardData;
  onChange?: (data: {
    exercises: Exercise[];
    selectedRoutines: string[];
    checkboxes: boolean[];
    additionalMarkings: boolean[];
    validationStatus: {
      isComplete: boolean;
      isFirstExerciseValid: boolean;
      isFullyValid: boolean;
      isFirstExerciseSelected: boolean;
    };
  }) => void;
  readOnly?: boolean;
  competitionGroup?: string;
}

// Custom styled components
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[3],
}));

const ExerciseHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(2),
}));

const MoveInput = styled('input')<{ error?: boolean; readOnly?: boolean }>(({ theme, error, readOnly }) => ({
  flexGrow: 1,
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${error ? theme.palette.error.main : readOnly ? theme.palette.action.disabledBackground : theme.palette.divider}`,
  backgroundColor: error ? theme.palette.error.light : readOnly ? theme.palette.action.disabledBackground : 'inherit',
  cursor: readOnly ? 'not-allowed' : 'text',
  color: readOnly ? theme.palette.text.disabled : theme.palette.text.primary,
  '&:focus': {
    outline: readOnly ? 'none' : undefined,
    borderColor: readOnly ? theme.palette.action.disabledBackground : theme.palette.primary.main,
    boxShadow: readOnly ? 'none' : `0 0 0 1px ${theme.palette.primary.main}`
  }
}));

// Komponente mit optionalen Props definieren
const TrampolineScorecard: React.FC<TrampolineScorecardProps> = ({
  initialData,
  onChange,
  readOnly = false,
  competitionGroup
}) => {
  // State for each exercise - 3 exercises, each with 10 moves
  const [exercises, setExercises] = useState<string[][]>(
    initialData?.exercises 
      ? initialData.exercises.map(ex => ex.moves) 
      : [
          Array(10).fill(''), // Exercise 1
          Array(10).fill(''), // Exercise 2
          Array(10).fill('')  // Exercise 3
        ]
  );

  // State for validation errors
  const [validationErrors, setValidationErrors] = useState<boolean[][]>([
    Array(10).fill(false), // Errors for exercise 1
    Array(10).fill(false), // Errors for exercise 2
    Array(10).fill(false)  // Errors for exercise 3
  ]);

  // State for duplicates
  const [duplicates, setDuplicates] = useState<boolean[][]>([
    Array(10).fill(false), // Duplicates for exercise 1
    Array(10).fill(false), // Duplicates for exercise 2
    Array(10).fill(false)  // Duplicates for exercise 3
  ]);

  // State for difficulty scores
  const [difficultyScores, setDifficultyScores] = useState<number[][]>([
    Array(10).fill(0), // Scores for exercise 1
    Array(10).fill(0), // Scores for exercise 2
    Array(10).fill(0)  // Scores for exercise 3
  ]);

  // State for total difficulty
  const [totalDifficulty, setTotalDifficulty] = useState<number[]>(
    initialData?.exercises
      ? initialData.exercises.map(ex => ex.totalDifficulty || 0)
      : [0, 0, 0]
  );
  
  // State for checkboxes in the first exercise - to mark required moves
  const [checkboxes, setCheckboxes] = useState<boolean[]>(
    initialData?.checkboxes || Array(10).fill(false)
  );
  
  // State for additional markings in first exercise
  const [additionalMarkings, setAdditionalMarkings] = useState<boolean[]>(
    initialData?.additionalMarkings || Array(10).fill(false)
  );
  
  // State for selected routines for each exercise
  const [selectedRoutines, setSelectedRoutines] = useState<string[]>(
    initialData?.selectedRoutines || ["", "", ""]
  );
  
  // State for requirement validation
  const [requirementsMet, setRequirementsMet] = useState<{[key: string]: boolean}>({});
  
  // State for confirmation dialogs
  const [showOverwriteDialog, setShowOverwriteDialog] = useState(false);
  const [showCheckboxResetDialog, setShowCheckboxResetDialog] = useState(false);
  const [pendingRoutineChange, setPendingRoutineChange] = useState<{
    exerciseIndex: number, 
    routineName: string, 
    overwriteMoves: boolean,
    resetCheckboxes?: boolean
  } | null>(null);
  
  // Ref to prevent automatic checkbox updates when user explicitly chose not to reset
  const skipAutoCheckboxUpdateRef = useRef<boolean>(false);

  // Berechne den Validierungsstatus für den Dialog
  const calculateValidationStatus = useCallback(() => {
    // Prüfen, ob für die erste Übung eine Übung ausgewählt wurde
    const isFirstExerciseSelected = selectedRoutines[0] !== "";

    // Prüfen, ob die ersten beiden Übungen vollständig sind (10 Sprünge je Übung)
    // Die dritte Übung ist optional
    const isComplete = exercises.slice(0, 2).every(exercise =>
      exercise.length === 10 && exercise.every(move => move && move.trim() !== "")
    ) && isFirstExerciseSelected;

    // Prüfen, ob die erste Übung fehlerfrei ist
    const isFirstExerciseValid = exercises[0].length === 10 &&
                                exercises[0].every(move => move && move.trim() !== "") &&
                                !validationErrors[0].some(error => error) &&
                                !duplicates[0].some(dup => dup) &&
                                isFirstExerciseSelected;

    // Prüfen, ob alle Übungen fehlerfrei sind
    const isFullyValid = isComplete &&
                         !validationErrors.some(exerciseErrors => exerciseErrors.some(error => error)) &&
                         !duplicates.some(exerciseDups => exerciseDups.some(dup => dup));

    return {
      isComplete,
      isFirstExerciseValid,
      isFullyValid,
      isFirstExerciseSelected
    };
  }, [exercises, validationErrors, duplicates, selectedRoutines]);

  // Benachrichtige den Parent über Datenänderungen
  const notifyParent = useCallback(() => {
    if (onChange) {
      const validationStatus = calculateValidationStatus();
      
      onChange({
        exercises: [0, 1, 2].map(i => ({
          name: selectedRoutines[i] || '',
          moves: exercises[i],
          totalDifficulty: totalDifficulty[i] || 0
        })),
        selectedRoutines,
        checkboxes,
        additionalMarkings,
        validationStatus
      });
    }
  }, [exercises, selectedRoutines, checkboxes, additionalMarkings, totalDifficulty, onChange, calculateValidationStatus]);

  // Benachrichtige den Parent, wenn sich relevante Daten ändern
  useEffect(() => {
    notifyParent();
  }, [exercises, selectedRoutines, checkboxes, additionalMarkings, totalDifficulty, notifyParent]);

  
  // Check if an exercise has requirements
  const hasRequirements = (routineName: string): boolean => {
    return routineName in typedRequirementsData.exerciseRequirements;
  };

  // Check if an exercise has difficultyJumps requirement
  const hasDifficultyJumpsRequirement = (routineName: string): boolean => {
    if (!routineName || !typedRequirementsData.exerciseRequirements[routineName]) return false;
    return !!typedRequirementsData.exerciseRequirements[routineName].difficultyJumps;
  };
  
  // Get the number of required jumps for difficultyJumps
  const getRequiredDifficultyJumpsCount = (routineName: string): number => {
    if (!hasDifficultyJumpsRequirement(routineName)) return 0;
    return typedRequirementsData.exerciseRequirements[routineName].difficultyJumps || 0;
  };

  // Handle move input change
  const handleMoveChange = (exerciseIndex: number, moveIndex: number, value: string): void => {
    // Im Nur-Lesen-Modus keine Änderungen vornehmen
    if (readOnly) return;

    const newExercises = [...exercises];
    newExercises[exerciseIndex][moveIndex] = value;
    setExercises(newExercises);
  };

  // Handle checkbox change
  const handleCheckboxChange = (moveIndex: number): void => {
    // Im Nur-Lesen-Modus keine Änderungen vornehmen
    if (readOnly) return;

    const newCheckboxes = [...checkboxes];
    newCheckboxes[moveIndex] = !newCheckboxes[moveIndex];
    setCheckboxes(newCheckboxes);
  };

  // Handle the second set of checkboxes
  const handleAdditionalMarkingChange = (moveIndex: number): void => {
    // Im Nur-Lesen-Modus keine Änderungen vornehmen
    if (readOnly) return;

    const newAdditionalMarkings = [...additionalMarkings];
    newAdditionalMarkings[moveIndex] = !newAdditionalMarkings[moveIndex];
    setAdditionalMarkings(newAdditionalMarkings);
  };

  // Function to copy exercise moves from one exercise to another
  const copyExercise = (fromIndex: number, toIndex: number): void => {
    // Im Nur-Lesen-Modus keine Änderungen vornehmen
    if (readOnly) return;

    const newExercises = [...exercises];
    newExercises[toIndex] = [...exercises[fromIndex]];
    setExercises(newExercises);
  };

  // Check if an exercise has any moves to copy
  const hasMovesToCopy = (exerciseIndex: number): boolean => {
    return exercises[exerciseIndex].some(move => move.trim() !== '');
  };

  // Check if an exercise has any moves filled
  const hasMovesInExercise = (exerciseIndex: number): boolean => {
    return exercises[exerciseIndex].some(move => move.trim() !== '');
  };

  // Apply routine change with or without overwriting moves
  const applyRoutineChange = (exerciseIndex: number, routineName: string, overwriteMoves: boolean, resetCheckboxes: boolean = false) => {
    // Set flag to control automatic checkbox updates BEFORE changing selectedRoutines
    if (exerciseIndex === 0) {
      if (!resetCheckboxes) {
        skipAutoCheckboxUpdateRef.current = true;
      } else {
        skipAutoCheckboxUpdateRef.current = false;
      }
    } 
    
    const newSelectedRoutines = [...selectedRoutines];
    newSelectedRoutines[exerciseIndex] = routineName;
    setSelectedRoutines(newSelectedRoutines);
    
    // Fill the exercise with predefined moves if overwrite is enabled or no moves exist
    if (overwriteMoves) {
      fillExercise(exerciseIndex, routineName);
    }
    
    // Reset checkboxes and additional markings when requested or when changing the first exercise with overwrite
    if (exerciseIndex === 0 && (resetCheckboxes || overwriteMoves)) {
      setCheckboxes(Array(10).fill(false));
      setAdditionalMarkings(Array(10).fill(false));
    }
  };

  // Handle overwrite dialog confirmation
  const handleOverwriteConfirm = () => {
    setShowOverwriteDialog(false);
    if (pendingRoutineChange) {
      const { exerciseIndex, routineName } = pendingRoutineChange;
      // Apply the routine change with overwrite - allow automatic checkbox updates
      skipAutoCheckboxUpdateRef.current = false;
      applyRoutineChange(exerciseIndex, routineName, true, true);
    }
    setPendingRoutineChange(null);
  };

  // Handle overwrite dialog cancellation
  const handleOverwriteCancel = () => {
    setShowOverwriteDialog(false);
    if (pendingRoutineChange) {
      const { exerciseIndex, routineName } = pendingRoutineChange;
      
      // For first exercise, show checkbox reset dialog
      if (exerciseIndex === 0) {
        // Update pending change to not overwrite moves and show checkbox dialog
        setPendingRoutineChange({ 
          exerciseIndex, 
          routineName, 
          overwriteMoves: false,
          resetCheckboxes: false 
        });
        setShowCheckboxResetDialog(true);
      } else {
        // For other exercises, apply change directly (no checkboxes involved)
        applyRoutineChange(exerciseIndex, routineName, false);
        setPendingRoutineChange(null);
      }
    }
  };

  // Handle checkbox reset dialog
  const handleCheckboxResetConfirm = () => {
    setShowCheckboxResetDialog(false);
    if (pendingRoutineChange) {
      const { exerciseIndex, routineName, overwriteMoves } = pendingRoutineChange;
      // Apply the routine change and reset checkboxes - allow automatic checkbox updates
      skipAutoCheckboxUpdateRef.current = false;
      applyRoutineChange(exerciseIndex, routineName, overwriteMoves, true);
    }
    setPendingRoutineChange(null);
  };

  const handleCheckboxResetCancel = () => {
    setShowCheckboxResetDialog(false);
    if (pendingRoutineChange) {
      const { exerciseIndex, routineName, overwriteMoves } = pendingRoutineChange;
      // Apply the routine change without resetting checkboxes - skip automatic updates
      applyRoutineChange(exerciseIndex, routineName, overwriteMoves, false);
    }
    setPendingRoutineChange(null);
  };

  // Erlaubte Übungen für die erste Übung berechnen
  const allowedExercisesForFirstExercise: string[] = competitionGroup 
    ? getAllowedExerciseIdsByClassName(competitionGroup)
    : Object.keys(typedExerciseData.predefinedExercises);

  // Function to handle routine selection change
  const handleRoutineChange = (exerciseIndex: number, routineName: string): void => {
    // Im Nur-Lesen-Modus keine Änderungen vornehmen
    if (readOnly) return;

    // Check if the exercise already has moves
    if (hasMovesInExercise(exerciseIndex)) {
      // Show confirmation dialog
      setPendingRoutineChange({ 
        exerciseIndex, 
        routineName, 
        overwriteMoves: true,
        resetCheckboxes: false 
      });
      setShowOverwriteDialog(true);
    } else {
      // No moves present, apply change directly
      // Only reset checkboxes for the first exercise
      //skipAutoCheckboxUpdateRef.current = false;
      applyRoutineChange(exerciseIndex, routineName, true, exerciseIndex === 0);
    }
  };

  // Function to pre-fill an exercise
  const fillExercise = (exerciseIndex: number, routineKey: string): void => {
    const predefinedExercises = typedExerciseData.predefinedExercises;
    
    // Type-safe property access
    if (routineKey in predefinedExercises) {
      const newExercises = [...exercises];
      newExercises[exerciseIndex] = [...predefinedExercises[routineKey]];
      setExercises(newExercises);
    }
  };

  // Mark required jumps based on the requirements
  useEffect(() => {
    const routineName = selectedRoutines[0];
    if (!routineName || !hasRequirements(routineName)) return;
    
    // Skip automatic checkbox updates if user explicitly chose not to reset
    if (skipAutoCheckboxUpdateRef.current) {
      skipAutoCheckboxUpdateRef.current = false; // Reset the flag for next time
      return;
    }
    
    const requirements = typedRequirementsData.exerciseRequirements[routineName];
    
    // Only mark if all 10 jumps are filled
    const allJumpsFilled = exercises[0].every(move => move.trim() !== '');
    if (!allJumpsFilled) return;
    
    // Create normalized versions for comparison
    const normalizedExercise = exercises[0].map(move => normalizeInput(move));
    
    // New checkboxes array
    const newCheckboxes = Array(10).fill(false);
    
    // Mark checkboxes for each required jump if specified
    if (requirements.jumps) {
      const normalizedRequiredJumps = requirements.jumps.map(jump => normalizeInput(jump));
      normalizedRequiredJumps.forEach(requiredJump => {
        const index = normalizedExercise.findIndex(move => move === requiredJump);
        if (index !== -1) {
          newCheckboxes[index] = true;
        }
      });
    }
    
    // Mark for landingsAnd if specified
    if (requirements.landingsAnd) {
      requirements.landingsAnd.forEach(landing => {
        // Find first jump with this landing
        const index = normalizedExercise.findIndex(move => move.endsWith(landing));
        if (index !== -1) {
          newCheckboxes[index] = true;
        }
      });
    }
    
    // Mark for landingsOr if specified
    if (requirements.landingsOr) {
      // Find first jump that satisfies any landing requirement
      const index = normalizedExercise.findIndex(move => 
        requirements.landingsOr!.some(landing => move.endsWith(landing))
      );
      if (index !== -1) {
        newCheckboxes[index] = true;
      }
    }
    
    // Mark for jumpStyle if specified
    if (requirements.jumpStyle) {
      const { leastRotation, leastTwist } = requirements.jumpStyle;
      
      // Find matching jumps
      normalizedExercise.forEach((move, index) => {
        if (!move) return;
        const rotationValue = parseInt(move.charAt(0), 10);
        const twistSum = move.length > 1 ? parseInt(move.charAt(1), 10) : 0;
        
        if (rotationValue >= leastRotation && twistSum >= leastTwist) {
          newCheckboxes[index] = true;
        }
      });
    }
    
    // Mark for twistRotation if specified
    if (requirements.twistRotation) {
      const { leastRotation, leastTwist } = requirements.twistRotation;
      
      // Find matching jumps
      normalizedExercise.forEach((move, index) => {
        if (!move) return;
        const rotationValue = parseInt(move.charAt(0), 10);
        const twistSum = move.length > 1 ? parseInt(move.charAt(1), 10) : 0;
        
        if (rotationValue >= leastRotation && twistSum >= leastTwist) {
          newCheckboxes[index] = true;
        }
      });
    }
    
    // Im Nur-Lesen-Modus keine automatische Setzung der Checkboxen
    if (!readOnly) {
      setCheckboxes(newCheckboxes);
    }
  }, [exercises[0].join('|'), selectedRoutines[0], readOnly]);

  // Update validations and calculations when exercises or requirements change
useEffect(() => {
  // Validate each move
  const newValidationErrors = exercises.map((exercise, exerciseIndex) => 
    exercise.map((move, moveIndex) => 
      move ? !validateMove(move, moveIndex, exerciseIndex, exercises) : false
    )
  );
  setValidationErrors(newValidationErrors);

  // Check for duplicates
  const newDuplicates = exercises.map((_, exerciseIndex) => 
    checkDuplicates(exerciseIndex, exercises)
  );
  setDuplicates(newDuplicates);

  // For first exercise, calculate difficulty based on routine type
  let newDifficultyScores = [...difficultyScores];
  const routineName = selectedRoutines[0];
  
  if (routineName === "Kür") {
    // For "Kür", calculate difficulty for all moves automatically
    newDifficultyScores[0] = exercises[0].map(move => 
      getDifficultyForSymbol(move, typedDifficultyData)
    );
  } else if (hasDifficultyJumpsRequirement(routineName)) {
    // Calculate only for marked moves in first exercise
    newDifficultyScores[0] = exercises[0].map((move, index) => 
      additionalMarkings[index] ? getDifficultyForSymbol(move, typedDifficultyData) : 0
    );
  } else {
    // If no difficulty requirements, don't calculate for first exercise
    newDifficultyScores[0] = Array(10).fill(0);
  }
  
  // Always calculate for exercises 2 and 3
  newDifficultyScores[1] = exercises[1].map(move => 
    getDifficultyForSymbol(move, typedDifficultyData)
  );
  newDifficultyScores[2] = exercises[2].map(move => 
    getDifficultyForSymbol(move, typedDifficultyData)
  );
  
  setDifficultyScores(newDifficultyScores);

  // Calculate total difficulty for each exercise
  const newTotalDifficulty = calculateTotalDifficulty(newDifficultyScores);
  setTotalDifficulty(newTotalDifficulty);
  
  // Validate requirements for the first exercise if applicable
  if (hasRequirements(routineName)) {
    // Get normalized exercise
    const normalizedExercise: string[] = exercises[0].map(move => normalizeInput(move));
    
    // Check for less270 requirement - count moves with rotation < 270°
    const movesWithLessRotation = normalizedExercise.filter((move: string) => {
      // Empty move counts as less than 270° rotation
      if (!move) return true;
      const firstDigit = move.charAt(0);
      return ['0', '1', '2'].includes(firstDigit);
    }).length;
    
    // Check for difficultyJumps requirement
    const requiredDifficultyJumps = getRequiredDifficultyJumpsCount(routineName);
    let markedDifficultyJumps;
    
    if (routineName === "Kür") {
      // For "Kür", count all non-empty moves as difficulty jumps
      markedDifficultyJumps = exercises[0].filter(move => move && move.trim() !== '').length;
    } else {
      // For other routines, count only marked moves
      markedDifficultyJumps = additionalMarkings.filter(isMarked => isMarked).length;
    }
    
    // For jumpStyle requirement
    let jumpStyleMet = false;
    if (typedRequirementsData.exerciseRequirements[routineName].jumpStyle) {
      const { leastRotation, leastTwist, leastAmount = 1 } = typedRequirementsData.exerciseRequirements[routineName].jumpStyle!;
      
      // Count jumps that meet the requirements
      const matchingJumps = normalizedExercise.filter((move: string) => {
        if (!move) return false;
        // Extract rotation and twist values
        // This is simplified and should be replaced with proper parsing logic
        const rotationValue = parseInt(move.charAt(0), 10);
        const twistSum = move.length > 1 ? parseInt(move.charAt(1), 10) : 0;
        
        return rotationValue >= leastRotation && twistSum >= leastTwist;
      }).length;
      
      jumpStyleMet = matchingJumps >= leastAmount;
    }
    
    // For twistRotation requirement
    let twistRotationMet = false;
    if (typedRequirementsData.exerciseRequirements[routineName].twistRotation) {
      const { leastRotation, leastTwist, leastAmount = 1 } = typedRequirementsData.exerciseRequirements[routineName].twistRotation!;
      
      // Count jumps that meet the requirements
      const matchingJumps = normalizedExercise.filter((move: string) => {
        if (!move) return false;
        // Extract rotation and twist values
        // This is simplified and should be replaced with proper parsing logic
        const rotationValue = parseInt(move.charAt(0), 10);
        const twistSum = move.length > 1 ? parseInt(move.charAt(1), 10) : 0;
        
        return rotationValue >= leastRotation && twistSum >= leastTwist;
      }).length;
      
      twistRotationMet = matchingJumps >= leastAmount;
    }
    
    // Get other requirement validations
    const requirementsValidation = validateRequirements(
      0, exercises, difficultyScores, checkboxes, routineName, typedRequirementsData
    );
    
    // Override or add our own validations
    const requiredLess270 = typedRequirementsData.exerciseRequirements[routineName].less270;
    if (requiredLess270 !== undefined) {
      requirementsValidation.less270 = movesWithLessRotation <= requiredLess270;
    }
    
    if (requiredDifficultyJumps) {
      requirementsValidation.difficultyJumps = markedDifficultyJumps === requiredDifficultyJumps;
    }
    
    // Override jumpStyle and twistRotation with our calculations
    if (typedRequirementsData.exerciseRequirements[routineName].jumpStyle) {
      requirementsValidation.jumpStyle = jumpStyleMet;
    }
    
    if (typedRequirementsData.exerciseRequirements[routineName].twistRotation) {
      requirementsValidation.twistRotation = twistRotationMet;
    }
    
    setRequirementsMet(requirementsValidation);
  } else {
    setRequirementsMet({});
  }
}, [exercises, checkboxes, additionalMarkings, selectedRoutines[0]]);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <StyledPaper>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" fontWeight="bold">
            WETTKAMPFKARTE
          </Typography>
          <Typography variant="h6">Trampolinturnen</Typography>
        </Box>

        {/* Exercises */}
        <Grid container spacing={3} sx={{ '& > :first-of-type': { flexGrow: 1 } }}>
          {[0, 1, 2].map((exerciseIndex) => (
            <Grid item xs={12} md={exerciseIndex === 0 ? 5 : 3.5} key={`exercise-${exerciseIndex}`}>
              <StyledPaper elevation={2}>
                <ExerciseHeader>
                  <Typography variant="h6" fontWeight="bold">
                    {exerciseIndex + 1}. Übung
                  </Typography>
                  
                  {/* All exercises now have the same selection box */}
                  <Box>
                    <FormControl
                      size="small"
                      sx={{ minWidth: 120 }}
                      error={exerciseIndex === 0 && selectedRoutines[0] === "" && exercises[0].some(move => move.trim() !== "")}
                    >
                      <InputLabel id={`routine-select-${exerciseIndex}`}>Übung wählen</InputLabel>
                      <Select
                        labelId={`routine-select-${exerciseIndex}`}
                        value={selectedRoutines[exerciseIndex]}
                        label="Übung wählen"
                        onChange={(e) => handleRoutineChange(exerciseIndex, e.target.value as string)}
                        disabled={readOnly}
                      >
                        <MenuItem value="">
                          <em>Auswählen</em>
                        </MenuItem>
                        {(exerciseIndex === 0 ? allowedExercisesForFirstExercise : Object.keys(typedExerciseData.predefinedExercises))
                          .filter(key => key in typedExerciseData.predefinedExercises)
                          .map(key => (
                            <MenuItem key={key} value={key}>{key}</MenuItem>
                          ))}
                      </Select>
                      {exerciseIndex === 0 && selectedRoutines[0] === "" && exercises[0].some(move => move.trim() !== "") && (
                        <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                          Bitte wählen Sie eine Übung für die 1. Übung aus.
                        </Typography>
                      )}
                    </FormControl>
                  </Box>
                  
                  <Typography variant="subtitle1" fontWeight="bold">SW</Typography>
                </ExerciseHeader>
                
                {/* Action buttons - im Nur-Lesen-Modus ausblenden */}
                {!readOnly && (
                  <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {/* Delete button */}
                    <Button 
                      variant="outlined" 
                      color="error" 
                      size="small"
                      onClick={() => {
                        const newExercises = [...exercises];
                        newExercises[exerciseIndex] = Array(10).fill('');
                        setExercises(newExercises);
                        
                        // Bei der ersten Übung auch Checkboxen und zusätzliche Markierungen zurücksetzen
                        if (exerciseIndex === 0) {
                          setCheckboxes(Array(10).fill(false));
                          setAdditionalMarkings(Array(10).fill(false));
                        }
                      }}                      
                    >
                      Löschen
                    </Button>
                    
                    {/* Copy button for second exercise (copy from first) */}
                    {exerciseIndex === 1 && (
                      <Button 
                        variant="outlined" 
                        color="primary" 
                        size="small"
                        onClick={() => copyExercise(0, 1)}
                        disabled={!hasMovesToCopy(0)}
                        title="Sprünge aus 1. Übung kopieren"
                      >
                        📋 Aus 1. kopieren
                      </Button>
                    )}
                    
                    {/* Copy button for third exercise (copy from second) */}
                    {exerciseIndex === 2 && (
                      <Button 
                        variant="outlined" 
                        color="primary" 
                        size="small"
                        onClick={() => copyExercise(1, 2)}
                        disabled={!hasMovesToCopy(1)}
                        title="Sprünge aus 2. Übung kopieren"
                      >
                        📋 Aus 2. kopieren
                      </Button>
                    )}
                  </Box>
                )}
                
                {/* Exercise inputs */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {Array.from({ length: 10 }).map((_, moveIndex) => (
                    <Box 
                      key={`move-${exerciseIndex}-${moveIndex}`} 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        gap: 1 
                      }}
                    >
                      <Typography sx={{ width: 24, textAlign: 'center', fontWeight: 'bold' }}>
                        {moveIndex + 1}
                      </Typography>
                      
                      {/* Only show checkboxes for first exercise when it has requirements */}
                      {exerciseIndex === 0 && hasRequirements(selectedRoutines[0]) && (
                        <>
                          <Checkbox
                            checked={checkboxes[moveIndex]}
                            onChange={() => handleCheckboxChange(moveIndex)}
                            size="small"
                            disabled={readOnly}
                          />
                          {/* Additional checkbox for difficultyJumps requirement */}
                          {hasDifficultyJumpsRequirement(selectedRoutines[0]) && (
                            <Checkbox
                              checked={additionalMarkings[moveIndex]}
                              onChange={() => handleAdditionalMarkingChange(moveIndex)}
                              size="small"
                              color="secondary"
                              sx={{ '& .MuiSvgIcon-root': { color: 'pink' } }}
                              disabled={readOnly}
                            />
                          )}
                        </>
                      )}
                      
                      <MoveInput
                        value={exercises[exerciseIndex][moveIndex]}
                        onChange={(e) => handleMoveChange(exerciseIndex, moveIndex, e.target.value)}
                        error={validationErrors[exerciseIndex][moveIndex]}
                        readOnly={readOnly}
                        aria-label={`Bewegung ${moveIndex + 1} in Übung ${exerciseIndex + 1}`}
                        placeholder="Sprung eingeben"
                      />
                      <Box sx={{ width: 40, textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        {difficultyScores[exerciseIndex][moveIndex] > 0 ? 
                          difficultyScores[exerciseIndex][moveIndex].toFixed(1) : 
                          validationErrors[exerciseIndex][moveIndex] ? '-' : ''
                        }
                        {duplicates[exerciseIndex][moveIndex] && (
                          <Typography component="span" sx={{ ml: 1, color: 'error.main', fontWeight: 'bold' }}>
                            !
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                  
                  {/* Total difficulty */}
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    fontWeight: 'bold', 
                    mt: 2,
                    pt: 1,
                    borderTop: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Typography sx={{ flexGrow: 1, textAlign: 'right', pr: 2, fontWeight: 'bold' }}>
                      Gesamtschwierigkeit
                    </Typography>
                    <Box sx={{ width: 40, textAlign: 'right' }}>
                      {totalDifficulty[exerciseIndex].toFixed(1)}
                    </Box>
                  </Box>
                  
                  {/* Requirements validation for the first exercise */}
                  {exerciseIndex === 0 && hasRequirements(selectedRoutines[0]) && (
                    <Box sx={{ 
                      mt: 3, 
                      pt: 2, 
                      borderTop: '1px solid',
                      borderColor: 'divider'
                    }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Anforderungen für {selectedRoutines[0]}:
                      </Typography>
                      
                      <Box sx={{ pl: 2 }}>
                        {/* Required jumps (AND: all must be present) */}
                        {typedRequirementsData.exerciseRequirements[selectedRoutines[0]].jumps && (
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: requirementsMet.requiredJumps ? 'success.main' : 'error.main'
                          }}>
                            {requirementsMet.requiredJumps ? '✓' : '✗'} Erforderliche Sprünge
                          </Box>
                        )}

                        {/* Required jump groups (OR within each group) */}
                        {typedRequirementsData.exerciseRequirements[selectedRoutines[0]].jumpsOr?.map((group, index) => (
                          <Box key={index} sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: requirementsMet[`jumpsOr_${index}`] ? 'success.main' : 'error.main'
                          }}>
                            {requirementsMet[`jumpsOr_${index}`] ? '✓' : '✗'} {group.message}
                          </Box>
                        ))}
                        
                        {/* Minimum number of saltos */}
                        {typedRequirementsData.exerciseRequirements[selectedRoutines[0]].less270 !== undefined && (
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            color: requirementsMet.less270 ? 'success.main' : 'error.main'
                          }}>
                            {requirementsMet.less270 ? '✓' : '✗'} {typedRequirementsData.messages[`less270-${typedRequirementsData.exerciseRequirements[selectedRoutines[0]].less270}`]}
                          </Box>
                        )}
                        
                        {/* Required landings (AND) */}
                        {typedRequirementsData.exerciseRequirements[selectedRoutines[0]].landingsAnd && (
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            color: requirementsMet.landingsAnd ? 'success.main' : 'error.main'
                          }}>
                            {requirementsMet.landingsAnd ? '✓' : '✗'} {typedRequirementsData.messages.landingsAnd}
                          </Box>
                        )}
                        
                        {/* Required landings (OR) */}
                        {typedRequirementsData.exerciseRequirements[selectedRoutines[0]].landingsOr && (
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            color: requirementsMet.landingsOr ? 'success.main' : 'error.main'
                          }}>
                            {requirementsMet.landingsOr ? '✓' : '✗'} {typedRequirementsData.messages.landingsOr}
                          </Box>
                        )}
                        
                        {/* Twist and rotation requirements */}
                        {typedRequirementsData.exerciseRequirements[selectedRoutines[0]].twistRotation && (
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            color: requirementsMet.twistRotation ? 'success.main' : 'error.main'
                          }}>
                            {requirementsMet.twistRotation ? '✓' : '✗'} {
                              (() => {
                                const { leastRotation, leastTwist, leastAmount = 1 } = 
                                  typedRequirementsData.exerciseRequirements[selectedRoutines[0]].twistRotation!;
                                const messageKey = `twistRotation-${leastRotation}${leastTwist}${leastAmount}`;
                                return typedRequirementsData.messages[messageKey] || 
                                       typedRequirementsData.messages.twistRotation03; // fallback
                              })()
                            }
                          </Box>
                        )}

                        {/* Jump style requirements */}
                        {typedRequirementsData.exerciseRequirements[selectedRoutines[0]].jumpStyle && (
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            color: requirementsMet.jumpStyle ? 'success.main' : 'error.main'
                          }}>
                            {requirementsMet.jumpStyle ? '✓' : '✗'} {
                              (() => {
                                const { leastRotation, leastTwist, leastAmount = 1 } = 
                                  typedRequirementsData.exerciseRequirements[selectedRoutines[0]].jumpStyle!;
                                const messageKey = `jumpStyle-${leastRotation}${leastTwist}${leastAmount}`;
                                return typedRequirementsData.messages[messageKey] || 
                                       typedRequirementsData.messages.jumpStyle80; // fallback
                              })()
                            }
                          </Box>
                        )}
                        
                        {/* Difficulty jumps requirements */}
                        {typedRequirementsData.exerciseRequirements[selectedRoutines[0]].difficultyJumps && (
                          <Box sx={{ 
                            display: 'flex', 
                            alignItems: 'center',
                            color: requirementsMet.difficultyJumps ? 'success.main' : 'error.main'
                          }}>
                            {requirementsMet.difficultyJumps ? '✓' : '✗'} {typedRequirementsData.messages.difficultyJumps}
                            {!requirementsMet.difficultyJumps && (
                              <Typography variant="caption" sx={{ ml: 1, color: 'error.main' }}>
                                (Genau {typedRequirementsData.exerciseRequirements[selectedRoutines[0]].difficultyJumps} Sprünge markieren)
                              </Typography>
                            )}
                          </Box>
                        )}
                        
                        {/* Different jumps requirement */}
                        {typedRequirementsData.exerciseRequirements[selectedRoutines[0]].differentJumps && (
                          <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: requirementsMet.differentJumps ? 'success.main' : 'error.main'
                          }}>
                            {requirementsMet.differentJumps ? '✓' : '✗'} {typedRequirementsData.messages.differentJumps}
                          </Box>
                        )}

                        {/* Rotation with landing requirements */}
                        {typedRequirementsData.exerciseRequirements[selectedRoutines[0]].rotationWithLanding?.map((req) => {
                          const key = `rotationWithLanding-${req.landings.join('')}-${req.leastRotation}-${req.leastAmount}`;
                          return (
                            <Box key={key} sx={{
                              display: 'flex',
                              alignItems: 'center',
                              color: requirementsMet[key] ? 'success.main' : 'error.main'
                            }}>
                              {requirementsMet[key] ? '✓' : '✗'} {typedRequirementsData.messages[key]}
                            </Box>
                          );
                        })}

                        {/* Overall requirements status */}
                        <Box sx={{
                          mt: 1,
                          pt: 1,
                          borderTop: '1px dashed',
                          borderColor: 'divider',
                          fontWeight: 'bold',
                          color: Object.values(requirementsMet).every(value => value) ? 'success.main' : 'error.main'
                        }}>
                          {Object.values(requirementsMet).every(value => value) ? 
                            '✓ Alle Anforderungen erfüllt' : 
                            '✗ Nicht alle Anforderungen erfüllt'
                          }
                        </Box>
                      </Box>
                    </Box>
                  )}
                </Box>
              </StyledPaper>
            </Grid>
          ))}
        </Grid>

        {/* Informational text and legend */}
        <Box sx={{ mt: 4, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          {readOnly && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1, p: 1, bgcolor: 'action.disabledBackground', borderRadius: 1 }}>
              Die Wettkampfkarte ist im Nur-Lesen-Modus, da die Abgabefrist bereits abgelaufen ist.
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            Die Schwierigkeitswerte werden automatisch berechnet.
          </Typography>
          
          {hasRequirements(selectedRoutines[0]) && (
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 1,
              mt: 1
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Checkbox size="small" checked readOnly />
                <Typography variant="caption">Markierte Pflicht-Elemente</Typography>
              </Box>
              
              {hasDifficultyJumpsRequirement(selectedRoutines[0]) && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Checkbox size="small" sx={{ '& .MuiSvgIcon-root': { color: 'pink' } }} checked readOnly />
                  <Typography variant="caption">Schwierigkeits-Elemente</Typography>
                </Box>
              )}
            </Box>
          )}
          
          <Typography variant="caption" color="text.secondary">
            Hinweis: 'o'/'O' werden zu '°' und '\' wird zu '/' automatisch konvertiert.
          </Typography>
        </Box>

        {/* Confirmation Dialogs */}
        {/* Overwrite moves dialog */}
        <Dialog
          open={showOverwriteDialog}
          onClose={() => {
            setShowOverwriteDialog(false);
            setPendingRoutineChange(null);
          }}
          aria-labelledby="overwrite-dialog-title"
          aria-describedby="overwrite-dialog-description"
        >
          <DialogTitle id="overwrite-dialog-title">
            Übung überschreiben?
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="overwrite-dialog-description">
              In dieser Übung sind bereits Sprünge eingetragen. Sollen die vorhandenen Sprünge mit der neuen Übung überschrieben werden?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleOverwriteCancel} color="primary">
              Nein
            </Button>
            <Button onClick={handleOverwriteConfirm} color="primary" autoFocus>
              Ja
            </Button>
          </DialogActions>
        </Dialog>

        {/* Reset checkboxes dialog */}
        <Dialog
          open={showCheckboxResetDialog}
          onClose={() => {
            setShowCheckboxResetDialog(false);
            setPendingRoutineChange(null);
          }}
          aria-labelledby="checkbox-reset-dialog-title"
          aria-describedby="checkbox-reset-dialog-description"
        >
          <DialogTitle id="checkbox-reset-dialog-title">
            Checkboxen zurücksetzen?
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="checkbox-reset-dialog-description">
              Sollen die Checkboxen für die Übungsanforderungen zurückgesetzt werden?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCheckboxResetCancel} color="primary">
              Nein
            </Button>
            <Button onClick={handleCheckboxResetConfirm} color="primary" autoFocus>
              Ja
            </Button>
          </DialogActions>
        </Dialog>
      </StyledPaper>
    </Container>
  );
};

export default TrampolineScorecard;